import { LinkedContentContract } from '@polaris-customerweb/sharedcomponents.core';
import {
    Geofence,
    PlannedRide,
    TrackedRide,
    UserContentItem,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { includes } from 'lodash-es';
import { Collection } from './collection';
import { RideEvent } from './ride-event';

export class SelectedItems {
    constructor(
        public collections: Collection[] = [],
        public plannedRides: PlannedRide[] = [],
        public waypoints: Waypoint[] = [],
        public trackedRides: TrackedRide[] = [],
        public geofences: Geofence[] = [],
        public communityIds: string[] = [],
        public events: RideEvent[] = [],
        public shareId: string | undefined = undefined
    ) {}

    get userContentItems(): UserContentItem[] {
        return [
            ...this.trackedRides,
            ...this.waypoints,
            ...this.plannedRides,
            ...this.geofences,
            ...this.collections,
        ];
    }

    static fromUserContent(content: UserContentItem): SelectedItems {
        if (content instanceof Collection) {
            return SelectedItems.fromCollection(content);
        } else if (content instanceof PlannedRide) {
            return SelectedItems.fromPlannedRide(content);
        } else if (content instanceof TrackedRide) {
            return SelectedItems.fromTrackedRide(content);
        } else if (content instanceof Waypoint) {
            return SelectedItems.fromWaypoint(content);
        } else if (content instanceof Geofence) {
            return SelectedItems.fromGeofence(content);
        } else if (content instanceof RideEvent) {
            return SelectedItems.fromRideEvent(content);
        } else {
            return new SelectedItems();
        }
    }

    static fromPlannedRide(plannedRide: PlannedRide): SelectedItems {
        return new SelectedItems(undefined, [plannedRide]);
    }

    static fromTrackedRide(trackedRide: TrackedRide): SelectedItems {
        return new SelectedItems(undefined, undefined, undefined, [
            trackedRide,
        ]);
    }

    static fromWaypoint(waypoint: Waypoint): SelectedItems {
        return new SelectedItems(undefined, undefined, [waypoint]);
    }

    static fromCollection(collection: Collection): SelectedItems {
        return new SelectedItems([collection]);
    }

    static fromGeofence(geofence: Geofence): SelectedItems {
        return new SelectedItems(undefined, undefined, undefined, undefined, [
            geofence,
        ]);
    }

    static fromCommunityIds(ids: string[]) {
        return new SelectedItems(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            ids
        );
    }

    static fromRideEvent(rideEvent: RideEvent): SelectedItems {
        return new SelectedItems(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            [rideEvent]
        );
    }

    static fromShareItem(content: UserContentItem, shareId: string) {
        const item = this.fromUserContent(content);
        item.shareId = shareId;
        return item;
    }

    toggleItem(userContentItem: UserContentItem): SelectedItems {
        if (userContentItem instanceof Collection) {
            return this.toggleCollection(userContentItem);
        } else if (userContentItem instanceof Geofence) {
            return this.toggleGeofence(userContentItem);
        } else if (userContentItem instanceof PlannedRide) {
            return this.togglePlannedRide(userContentItem);
        } else if (userContentItem instanceof TrackedRide) {
            return this.toggleTrackedRide(userContentItem);
        } else if (userContentItem instanceof Waypoint) {
            return this.toggleWaypoint(userContentItem);
        } else {
            throw new Error(
                'Tried to toggle unsupported type in selected items'
            );
        }
    }

    toggleCollection(collection: Collection): SelectedItems {
        const existingIndex = this.collections.findIndex(
            (item) => item.id === collection.id
        );
        const collections =
            existingIndex >= 0
                ? this.collections.filter((item) => item.id !== collection.id)
                : [...this.collections, collection];

        return new SelectedItems(
            collections,
            [...this.plannedRides],
            [...this.waypoints],
            [...this.trackedRides],
            [...this.geofences]
        );
    }

    toggleGeofence(geofence: Geofence): SelectedItems {
        const existingIndex = this.geofences.findIndex(
            (item) => item.id === geofence.id
        );
        const geofences =
            existingIndex >= 0
                ? this.geofences.filter((item) => item.id !== geofence.id)
                : [...this.geofences, geofence];

        return new SelectedItems(
            [...this.collections],
            [...this.plannedRides],
            [...this.waypoints],
            [...this.trackedRides],
            geofences
        );
    }

    togglePlannedRide(plannedRide: PlannedRide): SelectedItems {
        const existingIndex = this.plannedRides.findIndex(
            (item) => item.id === plannedRide.id
        );
        const plannedRides =
            existingIndex >= 0
                ? this.plannedRides.filter((item) => item.id !== plannedRide.id)
                : [...this.plannedRides, plannedRide];

        return new SelectedItems(
            [...this.collections],
            plannedRides,
            [...this.waypoints],
            [...this.trackedRides],
            [...this.geofences]
        );
    }

    toggleTrackedRide(trackedRide: TrackedRide): SelectedItems {
        const existingIndex = this.trackedRides.findIndex(
            (item) => item.id === trackedRide.id
        );
        const trackedRides =
            existingIndex >= 0
                ? this.trackedRides.filter((item) => item.id !== trackedRide.id)
                : [...this.trackedRides, trackedRide];

        return new SelectedItems(
            [...this.collections],
            [...this.plannedRides],
            [...this.waypoints],
            trackedRides,
            [...this.geofences]
        );
    }

    toggleWaypoint(waypoint: Waypoint): SelectedItems {
        const existingIndex = this.waypoints.findIndex(
            (item) => item.id === waypoint.id
        );
        const waypoints =
            existingIndex >= 0
                ? this.waypoints.filter((item) => item.id !== waypoint.id)
                : [...this.waypoints, waypoint];

        return new SelectedItems(
            [...this.collections],
            [...this.plannedRides],
            waypoints,
            [...this.trackedRides],
            [...this.geofences]
        );
    }

    clone(): SelectedItems {
        return new SelectedItems(
            [...this.collections],
            [...this.plannedRides],
            [...this.waypoints],
            [...this.trackedRides],
            [...this.geofences]
        );
    }

    filterIds(ids: string[]): SelectedItems {
        return new SelectedItems(
            this.collections.filter(
                (collection) => !includes(ids, collection.id)
            ),
            this.plannedRides.filter(
                (plannedRide) => !includes(ids, plannedRide.id)
            ),
            this.waypoints.filter((waypoint) => !includes(ids, waypoint.id)),
            this.trackedRides.filter(
                (trackedRide) => !includes(ids, trackedRide.id)
            ),
            this.geofences.filter((geofence) => !includes(ids, geofence.id))
        );
    }

    totalCount(): number {
        return (
            this.trackedRides.length +
            this.plannedRides.length +
            this.waypoints.length +
            this.geofences.length +
            this.collections.length
        );
    }

    hasAny(): boolean {
        return this.hasAnyNonCollections() || this.collections.length > 0;
    }

    hasCollections(): boolean {
        return this.collections.length > 0;
    }

    hasAnyNonCollections(): boolean {
        return (
            this.trackedRides.length > 0 ||
            this.plannedRides.length > 0 ||
            this.waypoints.length > 0 ||
            this.geofences.length > 0
        );
    }

    hasAnyItems(): boolean {
        return (
            this.trackedRides.length > 0 ||
            this.plannedRides.length > 0 ||
            this.waypoints.length > 0 ||
            this.geofences.length > 0
        );
    }

    toItemDto(
        item: Collection | Geofence | PlannedRide | TrackedRide | Waypoint,
        riderId: string
    ): LinkedContentContract {
        return {
            entityType: item.type,
            id: item.id,
            ownerRiderId: riderId,
        };
    }

    toLinkedItemsDto(riderId: string): LinkedContentContract[] {
        return [
            ...this.trackedRides.map((item) => {
                return this.toItemDto(item, riderId);
            }),
            ...this.waypoints.map((item) => {
                return this.toItemDto(item, riderId);
            }),
            ...this.plannedRides.map((item) => {
                return this.toItemDto(item, riderId);
            }),
            ...this.geofences.map((item) => {
                return this.toItemDto(item, riderId);
            }),
            ...this.collections.map((item) => {
                return this.toItemDto(item, riderId);
            }),
        ];
    }

    toSelectedItemIds(): {
        geofences: string[];
        plannedRides: string[];
        trackedRides: string[];
        waypoints: string[];
        collections: string[];
    } {
        return {
            collections: this.collections.map((collection) => collection.id),
            geofences: this.geofences.map((geofence) => geofence.id),
            plannedRides: this.plannedRides.map(
                (plannedRide) => plannedRide.id
            ),
            trackedRides: this.trackedRides.map(
                (trackedRide) => trackedRide.id
            ),
            waypoints: this.waypoints.map((waypoint) => waypoint.id),
        };
    }
}
